import { fetchQuery, graphql } from 'react-relay';

import { APIConfig } from 'src/config';
import createConcurrencyLimiter from 'src/util/concurrency';
import {
  DATA_CONSUMED_KEY, DATA_GENERATED_KEY, DEFAULT_FETCH_QUERY_TIMEOUT, DEFAULT_MAX_RETRIES,
  ELEC_ENERGY_CONSUMED, ELEC_ENERGY_GENERATED,
  P1D, PT30M,
} from 'src/util/constants';
import { queryWithRetry } from 'src/util/graphql';
import BatchQueryError from 'src/util/graphqlErrors';
import logger from 'src/util/logger';
import { measureSince, nowMs } from 'src/util/perfMarks';
import {
  calculatePortfolioFetchConcurrency,
  calculatePortfolioTradeSetSummaryBatchPlan,
  calculatePortfolioTradeSetSummaryBatchSize,
  generateBatchTimeRanges,
} from 'src/util/tradeSetSummary/portfolioBatchCalculator';

import { PORTFOLIO_SHOW_QUERY_TYPES } from './constants';
import { PortfolioShowLoadingActions } from '../reducer/constants';

// Portfolio uses a higher meter page size than the shared constant (100) because
// Stage 1 meter metadata is lightweight; 500 per page reduces round trips
// for large portfolios (500 meters = 1 request instead of 5).
const METER_LIST_PAGE_SIZE = 500;

// Fallback rule page size used when the portfolio rule count is unknown
// (e.g. date-range reloads via getTimeSeriesData that reuse pre-fetched meters).
// The backend does not treat `first: null` as "no limit", so an explicit large
// number is required. When rulesCount IS known (full load), it is used directly
// as rulesPageSize — a tighter and semantically correct upper bound.
//
// Load is controlled via the *meter* page size (getRulesMeterPageSize), not by
// capping rules per meter. Capping rules + re-fetching overflow meters was
// evaluated and rejected: cursor tokens are incompatible between the nested and
// per-meter resolvers (forcing full re-fetches), and the resulting sub-query
// thundering herd caused backend 500 errors at scale.
// See: docs/brainstorms/2026-04-20-portfolio-batch-size-optimization-brainstorm.md
//      (Rejected Approach, lines 156–168)
//      docs/exec-plans/active/2026-04-21-feat-portfolio-batch-size-optimization-plan.md
//      (lines 214–218, 278–280)
const FALLBACK_RULES_PAGE_SIZE = 999999999;

// GraphQL Queries
// Query 1: Meters data for a portfolio. Paginated to handle portfolios with many meters.
const portfolioMetersQuery = graphql`
query dataBatchingPortfolioMetersQuery(
  $uuid: ID!,
  $first: Int,
  $after: String,
  $start: Timestamp!,
  $finish: Timestamp!
) @throwOnFieldError {
  portfolio(uuid:$uuid) {
    meters(first: $first, after: $after, start: $start, finish: $finish) {
      edges {
        cursor
        node {
          uuid
          identifier
          externalIdentifier
          active { start finish }
          title
          tradePointId
          primaryBillingPoint {
            identifier
          }
          property {
            uuid
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
}
`;

// Query 2: Portfolio-level rule count. Fetched once in parallel with Stage 1 meters.
// Used by getRulesMeterPageSize to calculate the optimal meter page size for Stage 2 (Rules).
const portfolioRuleCountQuery = graphql`
query dataBatchingPortfolioRuleCountQuery(
  $uuid: ID!,
  $start: Timestamp!,
  $finish: Timestamp!
) @throwOnFieldError {
  portfolio(uuid: $uuid) {
    rulesCount(start: $start, finish: $finish)
  }
}
`;

// Query 3: Portfolio-level trade set summary. Used as a compatibility fallback
// when Stage 1 cursor metadata is unavailable.
const portfolioTradeSetSummaryQuery = graphql`
query dataBatchingPortfolioTradeSetSummaryQuery(
  $uuid: ID!,
  $batchStart: Timestamp!,
  $batchFinish: Timestamp!,
  $timeZone: String!,
  $tradeAggregation: String!
) @throwOnFieldError {
  portfolio(uuid: $uuid) {
    tradeSetSummary(start: $batchStart, finish: $batchFinish, timeZone: $timeZone, aggregation: $tradeAggregation, groups: [SUMMARY_GROUP_TRADE_POINT, SUMMARY_GROUP_TRADE_RULE, SUMMARY_GROUP_DIRECTION, SUMMARY_GROUP_TRADE_TYPE]) {
      key {
        tradePointId
        ruleId
        direction
        type
      }
      data {
        range { start finish }
        buyerTradePointIds
        sellerTradePointIds
        directions
        types
        value
        volume
        averagePrice
        tradesCount
        intervalsCount
      }
    }
  }
}
`;

// Query 4: Source-first trade set summary. The current schema does not expose
// a tradePointIds argument on Portfolio.tradeSetSummary, so stable Stage 1
// meter cursor pages are used to scope each request. This keeps the GraphQL
// path as portfolio → meters → tradeSetSummary while avoiding a portfolio-wide
// resolver execution for every 30-minute interval.
const portfolioTradeSetSummarySourcePageQuery = graphql`
query dataBatchingPortfolioTradeSetSummarySourcePageQuery(
  $uuid: ID!,
  $pageSize: Int!,
  $after: String,
  $meterStart: Timestamp!,
  $meterFinish: Timestamp!,
  $batchStart: Timestamp!,
  $batchFinish: Timestamp!,
  $timeZone: String!,
  $tradeAggregation: String!
) @throwOnFieldError {
  portfolio(uuid: $uuid) {
    meters(first: $pageSize, after: $after, start: $meterStart, finish: $meterFinish) {
      edges {
        cursor
        node {
          uuid
          tradeSetSummary(start: $batchStart, finish: $batchFinish, timeZone: $timeZone, aggregation: $tradeAggregation, groups: [SUMMARY_GROUP_TRADE_POINT, SUMMARY_GROUP_TRADE_RULE, SUMMARY_GROUP_DIRECTION, SUMMARY_GROUP_TRADE_TYPE]) {
            key {
              tradePointId
              ruleId
              direction
              type
            }
            data {
              range { start finish }
              buyerTradePointIds
              sellerTradePointIds
              directions
              types
              value
              volume
              averagePrice
              tradesCount
              intervalsCount
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
}
`;

// Query 3: Batched rules for a page of meters within a portfolio.
const portfolioRulesPageQuery = graphql`
query dataBatchingPortfolioRulesPageQuery(
  $uuid: ID!,
  $pageSize: Int!,
  $after: String,
  $start: Timestamp!,
  $finish: Timestamp!,
  $rulesPageSize: Int
) @throwOnFieldError {
  portfolio(uuid: $uuid) {
    meters(first: $pageSize, after: $after) {
      edges {
        node {
          uuid
          rules(start: $start, finish: $finish, first: $rulesPageSize) {
            edges {
              node {
                uuid
                tradeType
                buyer {
                  userId
                  user {
                    uuid
                    email
                    givenName
                    familyName
                  }
                  tradePoint {
                    uuid
                    type
                    meter {
                      uuid
                      identifier
                      title
                      property {
                        uuid
                        title
                      }
                    }
                  }
                }
                seller {
                  userId
                  user {
                    uuid
                    email
                    givenName
                    familyName
                  }
                  tradePoint {
                    uuid
                    type
                    meter {
                      uuid
                      identifier
                      title
                      property {
                        uuid
                        title
                      }
                    }
                  }
                }
                state
                clauses {
                  edges {
                    node {
                      price
                      ignorePublicHolidays
                      monthsOfYear
                      daysOfWeek
                      timesOfDay {
                        start { hours minutes }
                        finish { hours minutes }
                      }
                    }
                  }
                }
                proposedAt
                proposedBy {
                  uuid
                  email
                  givenName
                  familyName
                }
                proposedById
                acceptedAt
                acceptedBy {
                  uuid
                  email
                  givenName
                  familyName
                }
                acceptedById
                start
                finish
              }
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
}
`;

// Query 4: Batched meter data (consumed/generated) for a page of meters.
const portfolioMeterDataPageQuery = graphql`
query dataBatchingPortfolioMeterDataPageQuery(
  $uuid: ID!,
  $pageSize: Int!,
  $after: String,
  $meterStart: Timestamp!,
  $meterFinish: Timestamp!,
  $start: Timestamp!,
  $finish: Timestamp!,
  $aggregation: String!,
  $timeZone: String!,
  $metric: String!
) @throwOnFieldError {
  portfolio(uuid: $uuid) {
    meters(first: $pageSize, after: $after, start: $meterStart, finish: $meterFinish) {
      edges {
        node {
          uuid
          calculatedData(start: $start, finish: $finish, aggregation: $aggregation, timeZone: $timeZone, metric: $metric) {
            aggregation
            metric {
              identifier
            }
            timeZone
            timeRange {
              start
              finish
            }
            data {
              timestamp
              value
              flags {
                identifier
                description
                Valid
              }
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
}
`;

/**
 * Helper to wrap fetchQuery in a Promise with unsubscribe support for queryWithRetry.
 * @param {object} environment - Relay environment
 * @param {object} query - GraphQL query
 * @param {object} variables - Query variables
 * @returns {Promise} Promise with unsubscribe method
 */
const getQueryPromise = (environment, query, variables) => {
  let resolvePromise;
  let rejectPromise;

  const promise = new Promise((resolve, reject) => {
    resolvePromise = resolve;
    rejectPromise = reject;
  });

  const subscription = fetchQuery(environment, query, variables).subscribe({
    next: (result) => { resolvePromise(result); },
    error: (error) => { rejectPromise(error); },
  });

  promise.unsubscribe = () => subscription.unsubscribe();
  return promise;
};

// Historian record budgets (same as batchCalculator.js Property-level)
const MAX_RECORDS_PER_BATCH_PT30M = 8388; // payload limited: floor(1MB / 125B)
const MAX_RECORDS_PER_BATCH_P1D = 2000; // time limited: floor(20000ms / 10ms)
// Adaptive historian guardrails. The planner may choose smaller values based
// on meter count, aggregation, payload, authorization, and concurrency cost.
export const MAX_HISTORIAN_TIME_RANGE_DAYS = 90;
export const MAX_HISTORIAN_SOURCES_PER_BATCH = 60;
const MAX_HISTORIAN_INFLIGHT_SOURCES = 120;
const HISTORIAN_AUTHORIZATION_COST_UNITS = 10;
const MAX_HISTORIAN_REQUEST_COST_UNITS = 1200;

const HISTORIAN_BATCH_PROFILES = {
  [P1D]: {
    intervalHours: 24,
    maxIntervals: MAX_HISTORIAN_TIME_RANGE_DAYS,
    maxRecords: MAX_RECORDS_PER_BATCH_P1D,
    pointCostUnits: 1,
  },
  [PT30M]: {
    intervalHours: 0.5,
    maxIntervals: 7 * 48,
    maxRecords: MAX_RECORDS_PER_BATCH_PT30M,
    pointCostUnits: 0.1,
  },
};

/**
 * Calculate a source-first historian plan for one portfolio load.
 *
 * A historian request has two independent costs: authorizing each meter and
 * retrieving one value for each meter/interval pair. The plan maximizes the
 * time window first, then fits as many sources as possible within the
 * aggregation-specific record and request-cost budgets. The final concurrency
 * adjustment limits the number of source identifiers that can be in flight at
 * once without forcing small portfolios into unnecessarily small pages.
 * @param {object} params - Planning inputs.
 * @param {number} params.metersCount - Number of meters to fetch.
 * @param {number} params.totalIntervals - Intervals in the complete range.
 * @param {string} params.aggregation - Historian aggregation.
 * @param {number} params.concurrencyCap - Maximum concurrent requests.
 * @returns {object} Source-page and time-window plan.
 */
export const calculateHistorianBatchPlan = ({
  metersCount,
  totalIntervals,
  aggregation,
  concurrencyCap,
} = {}) => {
  const emptyPlan = {
    sourcePageSize: 0,
    intervalsPerBatch: 0,
    sourceBatchCount: 0,
    timeBatchCount: 0,
  };
  const validMetersCount = Number.isFinite(metersCount) && metersCount > 0
    ? Math.floor(metersCount)
    : 0;
  const validIntervalCount = Number.isFinite(totalIntervals) && totalIntervals > 0
    ? Math.ceil(totalIntervals)
    : 0;
  const profile = HISTORIAN_BATCH_PROFILES[aggregation];

  if (!validMetersCount || !validIntervalCount || !profile) return emptyPlan;

  const intervalsPerBatch = Math.min(validIntervalCount, profile.maxIntervals);
  const costPerMeter = HISTORIAN_AUTHORIZATION_COST_UNITS
    + (intervalsPerBatch * profile.pointCostUnits);
  const costLimitedPageSize = Math.floor(
    MAX_HISTORIAN_REQUEST_COST_UNITS / costPerMeter,
  );
  const payloadLimitedPageSize = Math.floor(profile.maxRecords / intervalsPerBatch);
  let sourcePageSize = Math.max(1, Math.min(
    validMetersCount,
    MAX_HISTORIAN_SOURCES_PER_BATCH,
    costLimitedPageSize,
    payloadLimitedPageSize,
  ));
  const normalizedConcurrencyCap = Number.isFinite(concurrencyCap)
    ? Math.max(1, Math.floor(concurrencyCap))
    : 1;
  const timeBatchCount = Math.ceil(validIntervalCount / intervalsPerBatch);

  // Recalculate the source cap after each adjustment. Reducing a page can
  // create more pages, so continue until the in-flight source budget is stable.
  // The source-page guardrail bounds the maximum number of iterations.
  for (let iteration = 0; iteration < MAX_HISTORIAN_SOURCES_PER_BATCH; iteration += 1) {
    const sourceBatchCount = Math.ceil(validMetersCount / sourcePageSize);
    const queuedHistorianRequests = 2 * sourceBatchCount * timeBatchCount;
    const activeRequestCount = Math.min(normalizedConcurrencyCap, queuedHistorianRequests);
    const concurrencyLimitedPageSize = Math.floor(
      MAX_HISTORIAN_INFLIGHT_SOURCES / activeRequestCount,
    );
    const nextPageSize = Math.max(1, Math.min(sourcePageSize, concurrencyLimitedPageSize));
    if (nextPageSize === sourcePageSize) {
      return {
        sourcePageSize,
        intervalsPerBatch,
        sourceBatchCount,
        timeBatchCount,
      };
    }
    sourcePageSize = nextPageSize;
  }

  return {
    sourcePageSize,
    intervalsPerBatch,
    sourceBatchCount: Math.ceil(validMetersCount / sourcePageSize),
    timeBatchCount,
  };
};

// Cost model constants for rules meter page sizing.
// Target: max rules per Stage 2 page, derived from empirical observation —
// 737 rules across 144 meters completed in ~14s on sandpit.
const MAX_RULES_PER_PAGE = 750;
const MIN_RULES_METERS_PER_PAGE = 5;

/**
 * Build historian source-page descriptors from Stage 1 meter edge cursors.
 * Returns an empty array when cursor metadata is incomplete so callers can
 * retain response-driven pagination as a compatibility fallback.
 * @param {object} params - Source-page inputs.
 * @param {Array} params.meters - Ordered Stage 1 meter nodes.
 * @param {Array} params.meterCursors - Ordered Stage 1 meter edge cursors.
 * @param {number} params.pageSize - Maximum meters per historian request.
 * @returns {Array} Ordered source-page descriptors.
 */
export const buildHistorianSourcePages = ({ meters, meterCursors, pageSize }) => {
  if (!Array.isArray(meters) || meters.length === 0
    || !Array.isArray(meterCursors) || meterCursors.length !== meters.length
    || !Number.isInteger(pageSize) || pageSize <= 0) {
    return [];
  }

  const sourcePages = [];
  for (let startIndex = 0; startIndex < meters.length; startIndex += pageSize) {
    const finishIndex = Math.min(startIndex + pageSize, meters.length);
    const afterCursor = startIndex === 0 ? null : meterCursors[startIndex - 1];
    if (!meterCursors[finishIndex - 1] || (startIndex > 0 && !afterCursor)) return [];

    sourcePages.push({
      afterCursor,
      expectedMeterIds: meters.slice(startIndex, finishIndex).map(({ uuid }) => uuid),
      pageIndex: sourcePages.length,
    });
  }

  return sourcePages;
};

/**
 * Calculate the optimal meter page size for Stage 2 (Rules) based on
 * portfolio-level rulesCount from the GraphQL schema.
 *
 * Unlike energy data (where page size depends on time range), rules page size
 * depends on rule density. This function uses the portfolio's `rulesCount`
 * field and a per-page rule target (MAX_RULES_PER_PAGE = 750) to determine
 * how many meters can be batched per Stage 2 request.
 *
 * **Algorithm:**
 * 1. Calculate average rules per meter: `rulesCount / metersCount`
 * 2. Calculate max meters per page: `floor(MAX_RULES_PER_PAGE / avgRulesPerMeter)`
 * 3. Clamp to [MIN_RULES_METERS_PER_PAGE, metersCount]
 * 4. Distribute evenly across pages so the last page is not a tiny remainder
 *
 * **Examples (MAX_RULES_PER_PAGE = 750):**
 *
 * | Meters | rulesCount | Avg rules/meter | Meters/page | Pages | Requests |
 * |--------|------------|-----------------|-------------|-------|----------|
 * | 150    | 500        | 3.3             | 150 (all)   | 1     | 1        |
 * | 150    | 1000       | 6.7             | 112         | 2     | 2        |
 * | 150    | 2000       | 13.3            | 56          | 3     | 3        |
 * | 285    | 1000       | 3.5             | 214         | 2     | 2        |
 * | 285    | 5000       | 17.5            | 42          | 7     | 7        |
 * @param {?number} rulesCount - Portfolio-level rule count from
 *   `portfolio.rulesCount`. When null/undefined (rule density unknown, e.g. the
 *   date-change reload path), meters per page are capped at
 *   `floor(MAX_RULES_PER_PAGE / metersCount)` — scaling conservatively with
 *   portfolio size — so large portfolios still split into multiple pages.
 * @param {number} metersCount - Total number of meters in the portfolio.
 * @returns {number} The optimal meter page size for Stage 2.
 *   Returns 0 when metersCount is 0 or invalid.
 */
export const getRulesMeterPageSize = (rulesCount, metersCount) => {
  if (!metersCount || metersCount <= 0) return 0;

  // rulesCount unknown — cap at floor(MAX_RULES_PER_PAGE / metersCount) meters
  // per page so the fallback scales with portfolio size: larger portfolios get
  // smaller pages and are never batched into one giant request.
  if (rulesCount == null) {
    const fallbackPageSize = Math.max(1, Math.floor(MAX_RULES_PER_PAGE / metersCount));
    const pageCount = Math.ceil(metersCount / fallbackPageSize);
    return Math.ceil(metersCount / pageCount);
  }

  // Genuinely zero (or negative) rules — nothing to page, fetch in one request.
  if (rulesCount <= 0) return metersCount;

  const avgRulesPerMeter = rulesCount / metersCount;

  // All meters fit in one page — avg rules per meter allows it
  if (avgRulesPerMeter * metersCount <= MAX_RULES_PER_PAGE) {
    return metersCount;
  }

  const rawPageSize = Math.floor(MAX_RULES_PER_PAGE / avgRulesPerMeter);
  const capped = Math.max(MIN_RULES_METERS_PER_PAGE, Math.min(rawPageSize, metersCount));

  // Distribute meters evenly across pages instead of one full + one small.
  const pageCount = Math.ceil(metersCount / capped);
  return Math.ceil(metersCount / pageCount);
};

/**
 * Reshape rules batch page response into the format expected by mergePortfolioBatchedData.
 * No rulesPageSize limit is applied — all rules for each meter are fetched.
 * @param {Array} meterEdges - Meter edges from the batch response.
 * @returns {Array} Array of { uuid, rules: { edges } } objects.
 */
const getRulesPageData = (meterEdges) => (
  meterEdges.map((edge) => ({
    uuid: edge.node.uuid,
    rules: { edges: edge.node?.rules?.edges || [] },
  }))
);

/**
 * Reshape meter data batch page response into the format expected by mergePortfolioBatchedData.
 * Maps calculatedData → dataConsumed or dataGenerated, with empty fallback for null data.
 * @param {Array} meterEdges - Meter edges from the batch response.
 * @param {string} dataKey - DATA_CONSUMED_KEY or DATA_GENERATED_KEY.
 * @param {object} fallbackMeta - Metadata for empty fallback shape.
 * @param {string} fallbackMeta.historianAggregation - Aggregation level.
 * @param {string} fallbackMeta.metric - Metric identifier.
 * @param {string} fallbackMeta.timezone - Timezone.
 * @param {number} fallbackMeta.batchStart - Start time in epoch seconds.
 * @param {number} fallbackMeta.batchFinish - Finish time in epoch seconds.
 * @returns {Array} Array of { uuid, dataConsumed/dataGenerated } objects.
 */
const reshapeMeterDataPage = (meterEdges, dataKey, fallbackMeta) => (
  meterEdges.map((edge) => {
    const { node } = edge;
    const calcData = node?.calculatedData;

    if (!calcData) {
      return {
        uuid: node.uuid,
        [dataKey]: {
          aggregation: fallbackMeta.historianAggregation,
          metric: { identifier: fallbackMeta.metric },
          timeZone: fallbackMeta.timezone,
          timeRange: { start: fallbackMeta.batchStart, finish: fallbackMeta.batchFinish },
          data: [],
        },
      };
    }

    return {
      uuid: node.uuid,
      [dataKey]: calcData,
    };
  })
);

/**
 * Merge multiple time-batches of meter data into a single array of per-meter results.
 * Each batch is an array of objects: { uuid, [dataKey]: calculatedData }.
 * Merging involves concatenating the `data` arrays within the calculatedData objects.
 * @param {Array<Array>} batches - Array of time-batches (each is an array of meter results).
 * @param {string} dataKey - DATA_CONSUMED_KEY or DATA_GENERATED_KEY.
 * @param {number} start - Outer start timestamp (epoch seconds).
 * @param {number} finish - Outer finish timestamp (epoch seconds).
 * @returns {Array} Array of merged { uuid, [dataKey]: calculatedData } objects.
 */
const getMergedTimeSeriesBatches = (batches, dataKey, start, finish) => {
  const resultsByMeterId = new Map();

  batches.forEach((batch) => {
    if (!Array.isArray(batch)) return;

    batch.forEach((meterResult) => {
      const { uuid } = meterResult;
      const calcData = meterResult[dataKey];

      if (!resultsByMeterId.has(uuid)) {
        // Create initial entry with outer time range. Guard calcData defensively
        // (optional chaining / empty-object spread) so a missing calculatedData
        // object cannot crash the whole merge, mirroring the merge-path guard below.
        resultsByMeterId.set(uuid, {
          ...(calcData || {}),
          timeRange: { start, finish },
          data: [...(calcData?.data || [])],
        });
      } else {
        const existing = resultsByMeterId.get(uuid);
        if (Array.isArray(existing.data) && Array.isArray(calcData?.data)) {
          existing.data = [...existing.data, ...calcData.data];
        }
      }
    });
  });

  return Array.from(resultsByMeterId.entries()).map(([uuid, data]) => ({
    uuid,
    [dataKey]: data,
  }));
};

/**
 * Increment a mutable ref's `.current` by 1 and return the new value.
 * @param {object} ref - Mutable ref object { current: number }.
 * @returns {number} The incremented value.
 */
const incrementRef = (ref) => {
  const mutableRef = ref;
  mutableRef.current += 1;
  return mutableRef.current;
};

/**
 * Signal cross-stage cancellation by setting the cancelled ref.
 * @param {object} ref - Cancellation token { current: boolean }.
 */
const signalCancellation = (ref) => {
  const mutableRef = ref;
  mutableRef.current = true;
};

/**
 * Fetch all pages of a batched stage query, accumulating reshaped results.
 * Uses recursion to paginate without await-in-loop (matching getPortfolioMeters pattern).
 * @param {object} params
 * @param {object} params.query - The GraphQL query to execute.
 * @param {object} params.baseVariables - Base query variables (without cursor).
 * @param {number} params.pageSize - Number of meters per page.
 * @param {string} params.stageName - Stage name for logging.
 * @param {string} params.queryType - Query-type tag (a member of
 *   `PORTFOLIO_SHOW_QUERY_TYPES`) applied to a `BatchQueryError` when a page
 *   fetch fails, so the fallback UI can render a query-specific message.
 * @param {Function} params.reshapeFunc - Function to reshape page response into output format.
 * @param {object} params.environment - Relay environment.
 * @param {object} [params.cancelledRef] - User-owned cancellation token { current: boolean }.
 * @param {object} [params.stageAbortRef] - Internal cross-stage abort token
 *   { current: boolean }; set when any stage fails so sibling stages stop
 *   paginating. Kept separate from cancelledRef so a stage failure is not
 *   mistaken for a user cancellation.
 * @param {object} [params.progressRef] - Shared mutable ref { current: number } for progress.
 * @param {number} [params.meterBatchesTotal] - Total batches across all stages for progress.
 * @param {Function} [params.dispatchFunc] - Reducer dispatch for progress updates.
 * @param {{ run: Function }} [params.limiter] - Optional shared concurrency limiter.
 *   When provided, each per-page `queryWithRetry` call is wrapped inside
 *   `limiter.run()` so the combined in-flight request count across all stages
 *   never exceeds the derived cap. Slots are held for a single page request
 *   (including its retries) and released before the next page is requested,
 *   preventing deadlocks at low caps.
 * @returns {Promise<Array>} Accumulated reshaped results across all pages.
 */
const getStagePages = async ({
  query, baseVariables, pageSize, stageName, queryType, reshapeFunc,
  environment, cancelledRef, stageAbortRef, progressRef, meterBatchesTotal, dispatchFunc,
  limiter,
}) => {
  const fetchPage = async (accumulated, afterCursor, pageIndex) => {
    if (cancelledRef?.current || stageAbortRef?.current) {
      logger.log(`${stageName}: Cancelled, stopping pagination`);
      return accumulated;
    }

    const variables = {
      ...baseVariables,
      pageSize,
      ...(afterCursor && { after: afterCursor }),
    };

    let data;
    try {
      const doFetch = () => queryWithRetry({
        queryFn: () => getQueryPromise(environment, query, variables),
        timeoutMs: APIConfig().GRAPH_API_TIMEOUT || DEFAULT_FETCH_QUERY_TIMEOUT,
        operationName: `${stageName} page ${pageIndex + 1}`,
        maxRetries: DEFAULT_MAX_RETRIES,
      });

      if (limiter) {
        data = await limiter.run(() => {
          // Re-check cancellation tokens after acquiring the slot: the task may
          // have been queued while a sibling stage failed and set stageAbortRef.
          if (cancelledRef?.current || stageAbortRef?.current) {
            logger.log(`${stageName}: Cancelled while queued, short-circuiting`);
            return Promise.resolve(null);
          }
          return doFetch();
        });
        // null is our post-queue cancellation sentinel (Relay `next` never emits
        // null), so treat it like a pre-fetch cancel regardless of ref state.
        if (data === null) {
          return accumulated;
        }
      } else {
        data = await doFetch();
      }
    } catch (error) {
      logger.error(`${stageName}: Fatal error on page ${pageIndex + 1}:`, error);
      // Signal abort via stageAbortRef (not cancelledRef) so the failure is not mistaken
      // for a user cancellation.
      if (stageAbortRef) {
        signalCancellation(stageAbortRef);
      }
      throw new BatchQueryError(queryType, error);
    }

    const metersConnection = data?.portfolio?.meters;
    if (!metersConnection?.edges) {
      logger.warn(`${stageName}: No meters data on page ${pageIndex + 1}`);
      return accumulated;
    }

    const { edges } = metersConnection;

    // Guard: stop if the backend returned an empty page (e.g. hasNextPage
    // was incorrectly true on the previous page).
    if (edges.length === 0) {
      logger.log(`${stageName}: Empty page ${pageIndex + 1}, stopping pagination`);
      return accumulated;
    }

    const pageResults = await reshapeFunc(edges);
    const allResults = [...accumulated, ...pageResults];
    const currentPage = pageIndex + 1;

    logger.log(`${stageName}: Page ${currentPage} fetched (${edges.length} meters, total accumulated: ${allResults.length})`);

    // Update progress
    if (progressRef && dispatchFunc && !cancelledRef?.current && !stageAbortRef?.current) {
      const loaded = incrementRef(progressRef);
      dispatchFunc({
        type: PortfolioShowLoadingActions.SET_METER_DATA_BATCH_PROGRESS,
        meterBatchesLoaded: loaded,
        meterBatchesTotal,
      });
    }

    const { pageInfo } = metersConnection;
    const nextCursor = pageInfo?.endCursor || '';
    const hasMore = pageInfo?.hasNextPage === true && nextCursor !== '';

    if (hasMore) {
      return fetchPage(allResults, nextCursor, currentPage);
    }
    return allResults;
  };

  return fetchPage([], null, 0);
};

// Fetch one time window for the current meter cursor page.
const getHistorianPageTimeBatch = async ({
  baseVariables, timeBatch, timeIndex, afterCursor, pageIndex, pageSize,
  dataKey, historianAggregation, metric, timezone, stageName, environment,
  cancelledRef, stageAbortRef, progressRef, meterBatchesTotal, dispatchFunc,
  limiter,
}) => {
  const batchStart = Math.floor(timeBatch.start.toSeconds());
  const batchFinish = Math.floor(timeBatch.finish.toSeconds());
  const variables = {
    ...baseVariables,
    pageSize,
    ...(afterCursor && { after: afterCursor }),
    meterStart: baseVariables.start,
    meterFinish: baseVariables.finish,
    start: batchStart,
    finish: batchFinish,
  };

  try {
    const data = await limiter.run(() => {
      if (cancelledRef?.current || stageAbortRef?.current) return Promise.resolve(null);

      return queryWithRetry({
        queryFn: () => getQueryPromise(environment, portfolioMeterDataPageQuery, variables),
        timeoutMs: APIConfig().GRAPH_API_TIMEOUT || DEFAULT_FETCH_QUERY_TIMEOUT,
        operationName: `${stageName} page ${pageIndex + 1}, time ${timeIndex + 1}`,
        maxRetries: DEFAULT_MAX_RETRIES,
      });
    });

    if (data === null) return null;

    const metersConnection = data?.portfolio?.meters;
    const edges = metersConnection?.edges || [];
    if (edges.length === 0) {
      return {
        results: [],
        dataKey,
        meterIds: [],
        pageInfo: metersConnection?.pageInfo,
        connectionFound: Boolean(metersConnection?.edges),
      };
    }

    const results = reshapeMeterDataPage(edges, dataKey, {
      historianAggregation,
      metric,
      timezone,
      batchStart,
      batchFinish,
    });

    if (progressRef && dispatchFunc && !cancelledRef?.current && !stageAbortRef?.current) {
      const loaded = incrementRef(progressRef);
      dispatchFunc({
        type: PortfolioShowLoadingActions.SET_METER_DATA_BATCH_PROGRESS,
        meterBatchesLoaded: loaded,
        meterBatchesTotal,
      });
    }

    return {
      results,
      dataKey,
      meterIds: edges.map(({ node }) => node.uuid),
      pageInfo: metersConnection.pageInfo,
      connectionFound: true,
    };
  } catch (error) {
    logger.error(`${stageName}: Fatal error on page ${pageIndex + 1}:`, error);
    if (stageAbortRef) signalCancellation(stageAbortRef);
    throw new BatchQueryError(PORTFOLIO_SHOW_QUERY_TYPES.METER_DATA, error);
  }
};

const hasMatchingHistorianPage = (reference, candidate) => (
  reference.connectionFound
  && candidate.connectionFound
  && reference.pageInfo?.hasNextPage === candidate.pageInfo?.hasNextPage
  && reference.pageInfo?.endCursor === candidate.pageInfo?.endCursor
  && reference.meterIds.length === candidate.meterIds.length
  && reference.meterIds.every((meterId, index) => meterId === candidate.meterIds[index])
);

// Precomputed source pages (historian and trade summary) come from Stage 1
// cursors, so a response is correct when it contains exactly the expected
// meters. Its pageInfo is not used to paginate, and the backend can report
// hasNextPage: true on the final page for small `first` values. Results are
// keyed by meter UUID, so order within the page does not matter. Missing,
// extra, or duplicated meters are rejected.
const hasExpectedSourcePageMeters = (sourcePage, result) => {
  const returnedMeterIds = new Set(result.meterIds);
  return result.connectionFound
    && result.meterIds.length === sourcePage.expectedMeterIds.length
    && returnedMeterIds.size === result.meterIds.length
    && sourcePage.expectedMeterIds.every((meterId) => returnedMeterIds.has(meterId));
};

const getHistorianPageBatches = (sourcePage, completedResults) => {
  const pageResults = completedResults.filter(({ pageIndex }) => (
    pageIndex === sourcePage.pageIndex
  ));
  const pagesMatch = pageResults.length > 0
    && pageResults.every((result) => hasExpectedSourcePageMeters(sourcePage, result));
  if (!pagesMatch) {
    throw new Error(`Historian source page ${sourcePage.pageIndex + 1} was inconsistent`);
  }

  return {
    consumedBatches: pageResults
      .filter(({ dataKey }) => dataKey === DATA_CONSUMED_KEY)
      .map(({ results }) => results),
    generatedBatches: pageResults
      .filter(({ dataKey }) => dataKey === DATA_GENERATED_KEY)
      .map(({ results }) => results),
  };
};

const getPrecomputedHistorianStagePages = async ({
  sourcePages, stageConfigs, commonVariables, timeBatches, pageSize,
  historianAggregation, timezone, environment, cancelledRef, stageAbortRef,
  progressRef, meterBatchesTotal, dispatchFunc, limiter,
}) => {
  const pageRequests = timeBatches.flatMap((timeBatch, timeIndex) => (
    stageConfigs.flatMap(({ dataKey, metric, stageName }) => (
      sourcePages.map(({ afterCursor, pageIndex }) => getHistorianPageTimeBatch({
        baseVariables: {
          ...commonVariables,
          aggregation: historianAggregation,
          timeZone: timezone,
          metric,
        },
        timeBatch,
        timeIndex,
        afterCursor,
        pageIndex,
        pageSize,
        dataKey,
        historianAggregation,
        metric,
        timezone,
        stageName,
        environment,
        cancelledRef,
        stageAbortRef,
        progressRef,
        meterBatchesTotal,
        dispatchFunc,
        limiter,
      }).then((result) => (result ? { ...result, pageIndex } : null)))
    ))
  ));
  const completedResults = (await Promise.all(pageRequests)).filter(Boolean);
  if (cancelledRef?.current || stageAbortRef?.current) {
    return { consumedBatches: [], generatedBatches: [] };
  }

  try {
    return sourcePages.reduce((accumulated, sourcePage) => {
      const pageBatches = getHistorianPageBatches(sourcePage, completedResults);
      logger.log(
        `Historian source page ${sourcePage.pageIndex + 1} fetched over `
        + `${timeBatches.length} time batches`,
      );
      return {
        consumedBatches: [...accumulated.consumedBatches, ...pageBatches.consumedBatches],
        generatedBatches: [...accumulated.generatedBatches, ...pageBatches.generatedBatches],
      };
    }, { consumedBatches: [], generatedBatches: [] });
  } catch (error) {
    signalCancellation(stageAbortRef);
    throw new BatchQueryError(PORTFOLIO_SHOW_QUERY_TYPES.METER_DATA, error);
  }
};

// Fetch historian data from precomputed source pages when Stage 1 cursors are
// available. Direct callers without cursors retain sequential pagination.
const getHistorianStagePages = async ({
  commonVariables, sourcePages, timeBatches, pageSize, historianAggregation, timezone,
  environment, cancelledRef, stageAbortRef, progressRef,
  meterBatchesTotal, dispatchFunc, limiter,
}) => {
  const stageConfigs = [
    {
      dataKey: DATA_CONSUMED_KEY,
      metric: ELEC_ENERGY_CONSUMED,
      stageName: 'Stage 3 (Consumed)',
    },
    {
      dataKey: DATA_GENERATED_KEY,
      metric: ELEC_ENERGY_GENERATED,
      stageName: 'Stage 4 (Generated)',
    },
  ];

  if (sourcePages.length > 0) {
    return getPrecomputedHistorianStagePages({
      sourcePages,
      stageConfigs,
      commonVariables,
      timeBatches,
      pageSize,
      historianAggregation,
      timezone,
      environment,
      cancelledRef,
      stageAbortRef,
      progressRef,
      meterBatchesTotal,
      dispatchFunc,
      limiter,
    });
  }

  const fetchPage = async (accumulated, afterCursor, pageIndex) => {
    if (cancelledRef?.current || stageAbortRef?.current) return accumulated;

    const pageRequests = stageConfigs.flatMap(({ dataKey, metric, stageName }) => (
      timeBatches.map((timeBatch, timeIndex) => getHistorianPageTimeBatch({
        baseVariables: {
          ...commonVariables,
          aggregation: historianAggregation,
          timeZone: timezone,
          metric,
        },
        timeBatch,
        timeIndex,
        afterCursor,
        pageIndex,
        pageSize,
        dataKey,
        historianAggregation,
        metric,
        timezone,
        stageName,
        environment,
        cancelledRef,
        stageAbortRef,
        progressRef,
        meterBatchesTotal,
        dispatchFunc,
        limiter,
      }))
    ));
    const timeResults = await Promise.all(pageRequests);
    const completedResults = timeResults.filter(Boolean);
    if (completedResults.length === 0) {
      return accumulated;
    }

    const [referencePage] = completedResults;
    if (!completedResults.every((result) => hasMatchingHistorianPage(referencePage, result))) {
      const error = new Error('Historian time batches returned inconsistent meter pages');
      signalCancellation(stageAbortRef);
      throw new BatchQueryError(PORTFOLIO_SHOW_QUERY_TYPES.METER_DATA, error);
    }
    if (referencePage.meterIds.length === 0) return accumulated;

    const consumedBatches = completedResults
      .filter(({ dataKey }) => dataKey === DATA_CONSUMED_KEY)
      .map(({ results }) => results);
    const generatedBatches = completedResults
      .filter(({ dataKey }) => dataKey === DATA_GENERATED_KEY)
      .map(({ results }) => results);
    const allResults = {
      consumedBatches: [...accumulated.consumedBatches, ...consumedBatches],
      generatedBatches: [...accumulated.generatedBatches, ...generatedBatches],
    };
    const { pageInfo } = referencePage;
    const nextCursor = pageInfo?.endCursor || '';
    const hasMore = pageInfo?.hasNextPage === true && nextCursor !== '';

    logger.log(
      `Historian source page ${pageIndex + 1} fetched over ${timeBatches.length} time batches`,
    );

    return hasMore
      ? fetchPage(allResults, nextCursor, pageIndex + 1)
      : allResults;
  };

  return fetchPage({ consumedBatches: [], generatedBatches: [] }, null, 0);
};

/**
 * Calculate total rules count across all meters
 * @param {Array} rulesData - Array of rules data objects with rules property
 * @returns {number} Total count of rules across all meters
 */
export const getTotalRulesCount = (rulesData) => {
  if (!rulesData || !Array.isArray(rulesData)) {
    return 0;
  }

  return rulesData.reduce((sum, meterRules) => {
    const rulesCount = meterRules?.rules?.edges?.length || 0;
    return sum + rulesCount;
  }, 0);
};

/**
 * Merges portfolio data from the five-stage fetch into the shape expected by buildMainData.
 * Output shape: { portfolio: { meters: { edges: [...] }, tradeSetSummary: [...] } }
 * @param {object} initialData - Base portfolio data (static fields)
 * @param {Array} meters - Array of meter objects from stage 1
 * @param {Array} rulesData - Array of { uuid, rules: { edges } } from stage 2
 * @param {Array} consumedData - Array of { uuid, dataConsumed } from stage 3
 * @param {Array} generatedData - Array of { uuid, dataGenerated } from stage 4
 * @param {Array} tradeSummaryBatches - Array of tradeSetSummary arrays from stage 5 batches
 * @returns {object} Merged portfolio data
 */
export const mergePortfolioBatchedData = (
  initialData,
  meters,
  rulesData,
  consumedData,
  generatedData,
  tradeSummaryBatches,
) => {
  const mergeStart = nowMs();
  const validMeters = Array.isArray(meters) ? meters : [];
  const validRulesData = Array.isArray(rulesData) ? rulesData : [];
  const validConsumedData = Array.isArray(consumedData) ? consumedData : [];
  const validGeneratedData = Array.isArray(generatedData) ? generatedData : [];
  const validBatches = Array.isArray(tradeSummaryBatches) ? tradeSummaryBatches : [];

  logger.log(`Merging five-stage data: ${validMeters.length} meters, ${validRulesData.length} rules, ${validConsumedData.length} consumed, ${validGeneratedData.length} generated, ${validBatches.length} trade summary batches`);

  // Create maps for efficient lookups
  const rulesByMeterId = new Map();
  const consumedByMeterId = new Map();
  const generatedByMeterId = new Map();

  validRulesData.forEach((meterRules) => {
    if (meterRules.uuid) {
      rulesByMeterId.set(meterRules.uuid, meterRules.rules);
    }
  });

  validConsumedData.forEach((meterConsumed) => {
    if (meterConsumed.uuid) {
      consumedByMeterId.set(meterConsumed.uuid, meterConsumed[DATA_CONSUMED_KEY]);
    }
  });

  validGeneratedData.forEach((meterGenerated) => {
    if (meterGenerated.uuid) {
      generatedByMeterId.set(meterGenerated.uuid, meterGenerated[DATA_GENERATED_KEY]);
    }
  });

  // Merge per-meter data
  const mergedEdges = validMeters.map((meter) => {
    const meterId = meter.uuid;
    return {
      node: {
        ...meter,
        rules: rulesByMeterId.get(meterId) || { edges: [] },
        [DATA_CONSUMED_KEY]: consumedByMeterId.get(meterId) || { data: [] },
        [DATA_GENERATED_KEY]: generatedByMeterId.get(meterId) || { data: [] },
      },
    };
  });

  // Merge portfolio-level trade set summary across all batches.
  // Each batch returns an array of trade summaries. We need to merge entries
  // with the same key (tradePointId, ruleId, direction, type) by concatenating data arrays.
  const tradeMap = new Map();

  validBatches.forEach((batchSummaries) => {
    if (!Array.isArray(batchSummaries)) return;

    batchSummaries.forEach((trade) => {
      if (!trade?.key) return;
      const {
        tradePointId, ruleId, direction, type,
      } = trade.key;
      const mapKey = `${tradePointId || ''}_${ruleId || ''}_${direction || ''}_${type || ''}`;

      if (tradeMap.has(mapKey)) {
        const existing = tradeMap.get(mapKey);
        if (Array.isArray(existing.data) && Array.isArray(trade.data)) {
          existing.data = [...existing.data, ...trade.data];
        }
      } else {
        tradeMap.set(mapKey, { ...trade, data: [...(trade.data || [])] });
      }
    });
  });

  const mergedTradeSetSummary = Array.from(tradeMap.values());

  // Shallow copy static portfolio fields — only top-level properties are
  // accessed (uuid, title, active, etc.). meters and tradeSetSummary are
  // overwritten below, so deep cloning is unnecessary.
  const basePortfolio = { ...(initialData?.portfolio || {}) };

  const merged = {
    portfolio: {
      ...basePortfolio,
      meters: { edges: mergedEdges },
      tradeSetSummary: mergedTradeSetSummary,
    },
  };
  if (mergeStart !== null) {
    measureSince('portfolio-show:mergePortfolioBatchedData', mergeStart);
  }
  return merged;
};

/**
 * Stage 1: Fetch all meters for a portfolio with pagination support.
 * Uses recursion to paginate without await-in-loop.
 * @param {object} args - Arguments for fetching meters
 * @param {string} args.portfolioId - Portfolio ID
 * @param {object} args.environment - Relay environment
 * @param {number} args.startSeconds - Start timestamp in epoch seconds
 * @param {number} args.finishSeconds - Finish timestamp in epoch seconds
 * @param {number} [args.pageSize=METER_LIST_PAGE_SIZE] - Page size
 * @returns {Promise<{meters: Array, meterCursors: Array}>} Paginated meters
 *   and their ordered edge cursors.
 */
export const getPortfolioMeters = async (args) => {
  const {
    portfolioId,
    environment,
    startSeconds,
    finishSeconds,
    pageSize = METER_LIST_PAGE_SIZE,
  } = args;

  const fetchPage = async (accumulatedMeters, accumulatedCursors, afterCursor) => {
    const variables = {
      uuid: portfolioId,
      first: pageSize,
      start: startSeconds,
      finish: finishSeconds,
      ...(afterCursor && { after: afterCursor }),
    };

    try {
      const data = await queryWithRetry({
        queryFn: () => getQueryPromise(environment, portfolioMetersQuery, variables),
        timeoutMs: APIConfig().GRAPH_API_TIMEOUT || DEFAULT_FETCH_QUERY_TIMEOUT,
        operationName: `Fetching meters for portfolio ${portfolioId}`,
        maxRetries: DEFAULT_MAX_RETRIES,
      });

      if (!data?.portfolio?.meters?.edges) {
        logger.warn('No meters data returned');
        return { meters: accumulatedMeters, meterCursors: accumulatedCursors };
      }

      const { edges } = data.portfolio.meters;

      // Guard: stop if the backend returned an empty page (e.g. hasNextPage
      // was incorrectly true on the previous page).
      if (edges.length === 0) {
        logger.log('Stage 1: Empty page, stopping pagination');
        return { meters: accumulatedMeters, meterCursors: accumulatedCursors };
      }

      const pageMeters = edges.map((edge) => edge.node);
      const pageCursors = edges.map((edge) => edge.cursor);
      const allMeters = [...accumulatedMeters, ...pageMeters];
      const allMeterCursors = [...accumulatedCursors, ...pageCursors];
      logger.log(`Stage 1: Fetched ${pageMeters.length} meters (total: ${allMeters.length})`);

      const { pageInfo } = data.portfolio.meters;
      const nextCursor = pageInfo?.endCursor || '';
      const hasMore = pageInfo?.hasNextPage === true && nextCursor !== '';

      if (hasMore) {
        return fetchPage(allMeters, allMeterCursors, nextCursor);
      }
      return { meters: allMeters, meterCursors: allMeterCursors };
    } catch (error) {
      logger.error('Error fetching portfolio meters page:', error);
      // Tag with the METERS stage so the fallback UI can render a
      // query-specific message. Re-throwing propagates to the outer
      // try/catch in getPortfolioDataInBatches and on to onError.
      throw new BatchQueryError(PORTFOLIO_SHOW_QUERY_TYPES.METERS, error);
    }
  };

  const result = await fetchPage([], [], '');
  logger.log(`getPortfolioMeters retrieved ${result.meters.length} meters`);
  return result;
};

/**
 * Fetch the portfolio-level rule count for a given time range.
 * Runs once in parallel with Stage 1 (meters) in getPortfolioDataInBatches.
 * The result is used by getRulesMeterPageSize to calculate the optimal meter
 * page size for Stage 2 (Rules).
 * @param {object} args - Arguments
 * @param {string} args.portfolioId - Portfolio UUID
 * @param {object} args.environment - Relay environment
 * @param {number} args.startSeconds - Start timestamp in epoch seconds
 * @param {number} args.finishSeconds - Finish timestamp in epoch seconds
 * @returns {Promise<number>} Portfolio rule count (0 when unavailable)
 * @throws {BatchQueryError} Tagged with `PORTFOLIO_SHOW_QUERY_TYPES.METERS`
 *   when the query fails, so the failure aborts parallel Stage 1 execution
 *   and surfaces to the `PlaceholderErrorModal` flow.
 */
export const getPortfolioRuleCount = async (args) => {
  const {
    portfolioId,
    environment,
    startSeconds,
    finishSeconds,
  } = args;

  const variables = {
    uuid: portfolioId,
    start: startSeconds,
    finish: finishSeconds,
  };

  try {
    const data = await queryWithRetry({
      queryFn: () => getQueryPromise(environment, portfolioRuleCountQuery, variables),
      timeoutMs: APIConfig().GRAPH_API_TIMEOUT || DEFAULT_FETCH_QUERY_TIMEOUT,
      operationName: `Fetching rule count for portfolio ${portfolioId}`,
      maxRetries: DEFAULT_MAX_RETRIES,
    });

    return data?.portfolio?.rulesCount ?? 0;
  } catch (error) {
    logger.error('Error fetching portfolio rule count:', error);
    // Tag with the METERS stage (consistent with the sibling Stage 1 meters
    // fetch) and re-throw so a field error aborts parallel Stage 1 execution
    // and surfaces to useBatchedFetchErrorState, rather than silently
    // proceeding with an incorrect rulesCount of 0.
    throw new BatchQueryError(PORTFOLIO_SHOW_QUERY_TYPES.METERS, error);
  }
};

/**
 * Stage 5 fallback: Fetch portfolio-level trade set summary for a single time batch.
 * @param {object} args - Arguments for fetching trade set summary
 * @param {string} args.portfolioId - Portfolio ID
 * @param {number} args.batchStart - Batch start time (epoch seconds)
 * @param {number} args.batchFinish - Batch finish time (epoch seconds)
 * @param {object} args.environment - Relay environment
 * @param {string} args.tradeAggregation - Aggregation level
 * @param {string} args.timezone - Timezone
 * @param {object} [args.cancelledRef] - User-owned cancellation token { current: boolean }.
 * @param {object} [args.stageAbortRef] - Internal cross-stage abort token
 *   { current: boolean }; set when any stage fails so sibling stages stop
 *   paginating. Kept separate from cancelledRef so a stage failure is not
 *   mistaken for a user cancellation.
 * @param {{ run: Function }} [args.limiter] - Optional shared concurrency limiter (see
 *   getStagePages for rationale).
 * @returns {Promise<Array>} Array of trade set summary objects
 */
export const getPortfolioTradeSetSummaryBatch = async (args) => {
  const {
    portfolioId,
    batchStart,
    batchFinish,
    environment,
    tradeAggregation,
    timezone,
    cancelledRef,
    stageAbortRef,
    limiter,
  } = args;

  if (cancelledRef?.current || stageAbortRef?.current) {
    logger.log(`Stage 5: Cancelled, skipping batch (${batchStart} - ${batchFinish})`);
    return [];
  }

  const variables = {
    uuid: portfolioId,
    batchStart,
    batchFinish,
    timeZone: timezone,
    tradeAggregation,
  };

  logger.log(`Stage 5: Fetching trade set summary for portfolio ${portfolioId} (${batchStart} - ${batchFinish})`);

  try {
    const doFetch = () => queryWithRetry({
      queryFn: () => getQueryPromise(
        environment,
        portfolioTradeSetSummaryQuery,
        variables,
      ),
      timeoutMs: APIConfig().GRAPH_API_TIMEOUT || DEFAULT_FETCH_QUERY_TIMEOUT,
      operationName: `Fetching trade set summary batch for portfolio ${portfolioId}`,
      maxRetries: DEFAULT_MAX_RETRIES,
    });

    let data;
    if (limiter) {
      data = await limiter.run(() => {
        // Re-check cancellation after acquiring the slot: the batch may have
        // been queued while a sibling batch failed and set stageAbortRef.
        if (cancelledRef?.current || stageAbortRef?.current) {
          logger.log(`Stage 5: Cancelled while queued, short-circuiting batch (${batchStart} - ${batchFinish})`);
          return Promise.resolve(null);
        }
        return doFetch();
      });
      // null is our post-queue cancellation sentinel (Relay `next` never emits
      // null); return empty (no error to propagate).
      if (data === null) {
        return [];
      }
    } else {
      data = await doFetch();
    }

    if (!data?.portfolio?.tradeSetSummary) {
      logger.warn('No trade set summary returned for batch');
      return [];
    }

    const summaryCount = data.portfolio.tradeSetSummary.length;
    logger.log(`Stage 5: Received ${summaryCount} trade set summary entries`);
    return data.portfolio.tradeSetSummary;
  } catch (error) {
    logger.error('Error fetching portfolio trade set summary batch:', error);
    // Abort sibling stages via the internal flag — NOT the user-owned
    // cancelledRef, matching the pattern in getStagePages.
    if (stageAbortRef) {
      signalCancellation(stageAbortRef);
    }
    // Tag with the TRADE_SET_SUMMARY stage and re-throw so the failure is
    // surfaced to the consumer's onError (rather than silently producing an
    // empty summary), matching the field-error handling in the other stages.
    throw new BatchQueryError(PORTFOLIO_SHOW_QUERY_TYPES.TRADE_SET_SUMMARY, error);
  }
};

/**
 * Fetch one source page of trade summaries for one time window.
 *
 * Stage 1 has already established the ordered meter connection. Reusing its
 * cursors makes this request deterministic and lets the backend validate only
 * the selected source page instead of resolving the complete portfolio for
 * every time window.
 * @param {object} args - Request arguments.
 * @param {string} args.portfolioId - Portfolio ID.
 * @param {object} args.sourcePage - Precomputed meter cursor page.
 * @param {number} args.batchStart - Batch start in epoch seconds.
 * @param {number} args.batchFinish - Batch finish in epoch seconds.
 * @param {number} args.meterStart - Full meter-range start in epoch seconds.
 * @param {number} args.meterFinish - Full meter-range finish in epoch seconds.
 * @param {object} args.environment - Relay environment.
 * @param {string} args.tradeAggregation - Aggregation level.
 * @param {string} args.timezone - Timezone.
 * @param {object} [args.cancelledRef] - User-owned cancellation token.
 * @param {object} [args.stageAbortRef] - Internal stage cancellation token.
 * @param {{ run: Function }} [args.limiter] - Shared concurrency limiter.
 * @returns {Promise<Array>} Trade summaries returned for the source page.
 */
export const getPortfolioTradeSetSummarySourceBatch = async (args) => {
  const {
    portfolioId,
    sourcePage,
    batchStart,
    batchFinish,
    meterStart,
    meterFinish,
    environment,
    tradeAggregation,
    timezone,
    cancelledRef,
    stageAbortRef,
    limiter,
  } = args;

  if (cancelledRef?.current || stageAbortRef?.current) return [];

  const variables = {
    uuid: portfolioId,
    pageSize: sourcePage.expectedMeterIds.length,
    ...(sourcePage.afterCursor && { after: sourcePage.afterCursor }),
    meterStart,
    meterFinish,
    batchStart,
    batchFinish,
    timeZone: timezone,
    tradeAggregation,
  };
  const doFetch = () => queryWithRetry({
    queryFn: () => getQueryPromise(
      environment,
      portfolioTradeSetSummarySourcePageQuery,
      variables,
    ),
    timeoutMs: APIConfig().GRAPH_API_TIMEOUT || DEFAULT_FETCH_QUERY_TIMEOUT,
    operationName: `Fetching source trade set summary batch ${sourcePage.pageIndex + 1}`,
    maxRetries: DEFAULT_MAX_RETRIES,
  });

  try {
    const data = limiter
      ? await limiter.run(() => {
        if (cancelledRef?.current || stageAbortRef?.current) return Promise.resolve(null);
        return doFetch();
      })
      : await doFetch();

    if (data === null) return [];

    const metersConnection = data?.portfolio?.meters;
    const edges = metersConnection?.edges || [];
    const result = {
      connectionFound: Boolean(metersConnection?.edges),
      meterIds: edges.map(({ node }) => node?.uuid),
      pageInfo: metersConnection?.pageInfo,
    };
    if (!hasExpectedSourcePageMeters(sourcePage, result)) {
      throw new Error(`Trade source page ${sourcePage.pageIndex + 1} was inconsistent`);
    }

    return edges.flatMap(({ node }) => node?.tradeSetSummary || []);
  } catch (error) {
    logger.error('Error fetching source portfolio trade set summary batch:', error);
    if (stageAbortRef) signalCancellation(stageAbortRef);
    throw new BatchQueryError(PORTFOLIO_SHOW_QUERY_TYPES.TRADE_SET_SUMMARY, error);
  }
};

// Spans may be split across several membership records. A null finish is
// open-ended. Once a gap is found, later (sorted) spans cannot close it.
const hasSpansCoveringRange = (spans, startSeconds, finishSeconds) => {
  const coveredUntil = spans
    .filter((span) => Number.isFinite(span?.start))
    .sort((a, b) => a.start - b.start)
    .reduce((covered, span) => (
      span.start > covered ? covered : Math.max(covered, span.finish ?? Infinity)
    ), startSeconds);
  return coveredUntil >= finishSeconds;
};

/**
 * The source-first trade query resolves Meter.tradeSetSummary, which does not
 * clamp trades to the portfolio's active range or to property membership. It
 * only matches the portfolio-level query when the portfolio and every meter's
 * property are members for the whole requested range. Returns false when that
 * cannot be confirmed, including when the membership list is truncated.
 * @param {object} params - Coverage inputs.
 * @param {object} [params.portfolio] - Static portfolio data with active and propertyMembers.
 * @param {Array} params.meters - Stage 1 meter nodes with property.uuid.
 * @param {number} params.startSeconds - Requested range start in epoch seconds.
 * @param {number} params.finishSeconds - Requested range finish in epoch seconds.
 * @returns {boolean} True when membership covers the whole range for every meter.
 */
export const hasFullMembershipCoverage = ({
  portfolio, meters, startSeconds, finishSeconds,
}) => {
  const memberEdges = portfolio?.propertyMembers?.edges;
  if (!portfolio?.active || !Array.isArray(memberEdges)
    || (portfolio.propertyMembers.totalCount ?? 0) > memberEdges.length
    || !hasSpansCoveringRange([portfolio.active], startSeconds, finishSeconds)) {
    return false;
  }

  const spansByPropertyId = new Map();
  memberEdges.forEach(({ node } = {}) => {
    const propertyId = node?.property?.uuid;
    if (!propertyId || !node.active) return;
    spansByPropertyId.set(propertyId, [...(spansByPropertyId.get(propertyId) || []), node.active]);
  });

  return meters.every((meter) => hasSpansCoveringRange(
    spansByPropertyId.get(meter?.property?.uuid) || [],
    startSeconds,
    finishSeconds,
  ));
};

const getPrecomputedTradeSummaryBatches = async ({
  portfolioId, sourcePages, timeBatches, startSeconds, finishSeconds,
  environment, tradeAggregation, timezone, cancelledRef, stageAbortRef, limiter,
  onBatchCompleteFunc,
}) => {
  const requests = timeBatches.flatMap((timeBatch) => sourcePages.map((sourcePage) => (
    getPortfolioTradeSetSummarySourceBatch({
      portfolioId,
      sourcePage,
      batchStart: Math.floor(timeBatch.start.toSeconds()),
      batchFinish: Math.floor(timeBatch.finish.toSeconds()),
      meterStart: startSeconds,
      meterFinish: finishSeconds,
      environment,
      tradeAggregation,
      timezone,
      cancelledRef,
      stageAbortRef,
      limiter,
    }).then((result) => {
      if (onBatchCompleteFunc) onBatchCompleteFunc();
      return result;
    })
  )));

  return Promise.all(requests);
};

/**
 * Stages 2-5: fetch rules, consumed, generated, and trade summaries — all in parallel.
 * Stage 5 (trade summary) uses the BE-provided portfolio.rulesCount from Stage 1 for
 * batch sizing, so it starts immediately alongside Stages 2-4 instead of waiting for
 * Stage 2's actual rule edges.
 * Shared by getPortfolioDataInBatches (full load) and getTimeSeriesData (date-change reload).
 * @param {object} params
 * @param {string} params.portfolioId - Portfolio UUID
 * @param {Array}  params.meters - Pre-fetched meter objects from stage 1
 * @param {Array}  [params.meterCursors] - Ordered Stage 1 meter edge cursors.
 * @param {object} params.timeRange - { start, finish } Luxon DateTimes
 * @param {string} params.timezone - Timezone string
 * @param {object} params.environment - Relay environment
 * @param {string} params.historianAggregation - Aggregation for energy data
 * @param {string} params.tradeAggregation - Aggregation for trade data
 * @param {Function} [params.dispatchFunc] - Reducer dispatch
 * @param {object}   [params.cancelledRef] - Cancellation token { current: boolean }
 * @param {number}   [params.rulesCount] - Portfolio-level rule count from the dedicated
 *   rulesCount query. Used as the rulesPageSize upper bound (tighter than the fallback
 *   999999999) and to size the Stage 2 meter batches via getRulesMeterPageSize.
 *   When omitted (e.g. date-range reload), both fall back to their respective defaults.
 * @param {object}   [params.initialData] - Static portfolio data. Its active range and
 *   property memberships decide whether Stage 5 can use the source-first query.
 * @returns {Promise<object|null>} { rulesData, consumedData, generatedData, tradeSummaryBatches,
 *   totalRulesCount, batchCount } or null if cancelled
 */
const fetchTimeSeriesStages = async ({
  portfolioId, meters, meterCursors, timeRange, timezone, environment,
  historianAggregation, tradeAggregation, dispatchFunc, cancelledRef,
  rulesCount, initialData,
}) => {
  // No meters — nothing to batch. Short-circuit to avoid issuing
  // portfolio.meters(first: 0) queries, which fail GraphQL validation.
  if (!meters || meters.length === 0) {
    logger.log('fetchTimeSeriesStages: no meters, skipping stages 2-5');
    return {
      rulesData: [],
      consumedData: [],
      generatedData: [],
      tradeSummaryBatches: [],
      totalRulesCount: 0,
      batchCount: 0,
    };
  }

  const { start, finish } = timeRange || {};
  const startSeconds = Math.floor(start.toSeconds());
  const finishSeconds = Math.floor(finish.toSeconds());

  // Size source batches against the longest historian window. This deliberately
  // favours fewer source identifiers and longer time ranges per request.
  const durationHours = finish.diff(start, 'hours').hours;
  const intervalHours = historianAggregation === P1D ? 24 : 0.5;
  const intervalsInRange = Math.ceil(durationHours / intervalHours);
  const metersCount = meters.length;
  const tradeSummaryRulesCount = rulesCount ?? MAX_RULES_PER_PAGE;
  const concurrencyCap = calculatePortfolioFetchConcurrency({
    rulesCount: tradeSummaryRulesCount,
    metersCount,
  });
  const historianPlan = calculateHistorianBatchPlan({
    metersCount,
    totalIntervals: intervalsInRange,
    aggregation: historianAggregation,
    concurrencyCap,
  });
  const { sourcePageSize, intervalsPerBatch: historianIntervalsPerBatch } = historianPlan;

  // Calculate decoupled page size for rules stage (rule-density-aware)
  const rulesMeterPageSize = getRulesMeterPageSize(rulesCount, metersCount);

  // Stage 5 batch sizing is computed up-front (before any stage promise is
  // created) so a synchronous throw here cannot strand the already-created
  // stage promises as unhandled rejections.
  // Uses rulesCount from Stage 1 (portfolio.rulesCount) for batch sizing so it
  // can start immediately — no need to wait for Stage 2's actual rule edges.
  // The planner applies a calibrated safety factor because the BE-provided
  // rulesCount is a lower bound (shared rules counted once) for meter-level
  // summary work.
  // When rulesCount is unknown (date-change reload path), fall back to a fixed
  // conservative MAX_RULES_PER_PAGE rather than metersCount, which would
  // under-estimate rule volume and risk overly large time batches.
  const batchCalculationResult = calculatePortfolioTradeSetSummaryBatchSize({
    start,
    finish,
    aggregation: tradeAggregation,
    rulesCount: tradeSummaryRulesCount,
    metersCount,
  });

  // Common variables for all stages
  const commonVariables = {
    uuid: portfolioId,
    start: startSeconds,
    finish: finishSeconds,
  };

  const timeBatches = generateBatchTimeRanges({
    start,
    finish,
    batchSizeHours: batchCalculationResult.batchSizeHours,
  });

  const tradeIntervalHours = tradeAggregation === P1D ? 24 : 0.5;
  const tradeIntervalsInRange = Math.ceil(durationHours / tradeIntervalHours);
  const tradePlan = calculatePortfolioTradeSetSummaryBatchPlan({
    metersCount,
    rulesCount: tradeSummaryRulesCount,
    totalIntervals: tradeIntervalsInRange,
    aggregation: tradeAggregation,
    concurrencyCap,
  });
  const tradeSourcePages = buildHistorianSourcePages({
    meters,
    meterCursors,
    pageSize: tradePlan.sourcePageSize,
  });
  const useSourceFirstTradeSummary = tradeSourcePages.length > 0
    && hasFullMembershipCoverage({
      portfolio: initialData?.portfolio, meters, startSeconds, finishSeconds,
    });
  const tradeTimeBatches = useSourceFirstTradeSummary
    ? generateBatchTimeRanges({
      start,
      finish,
      batchSizeHours: tradePlan.intervalsPerBatch * tradeIntervalHours,
    })
    : timeBatches;
  const batchCount = useSourceFirstTradeSummary
    ? tradeSourcePages.length * tradeTimeBatches.length
    : timeBatches.length;
  const historianTimeBatches = generateBatchTimeRanges({
    start,
    finish,
    batchSizeHours: historianIntervalsPerBatch * intervalHours,
  });
  const { sourceBatchCount, timeBatchCount: plannedHistorianTimeBatchCount } = historianPlan;
  const sourcePages = buildHistorianSourcePages({
    meters,
    meterCursors,
    pageSize: sourcePageSize,
  });
  const historianTimeBatchCount = historianTimeBatches.length || plannedHistorianTimeBatchCount;

  // Progress tracking: rules retain cursor pagination; each energy stage issues
  // one request for every source-batch/time-window pair.
  const rulesPages = Math.ceil(metersCount / Math.max(rulesMeterPageSize, 1));
  const historianRequestsPerStage = sourceBatchCount * historianTimeBatchCount;
  const meterBatchesTotal = rulesPages + (2 * historianRequestsPerStage);

  // Shared progress counter across all three stages (single-threaded JS — no race)
  const progressRef = { current: 0 };

  const stageAbortRef = { current: false };

  // Initialize meter batch progress
  if (dispatchFunc && !cancelledRef?.current) {
    dispatchFunc({
      type: PortfolioShowLoadingActions.SET_METER_DATA_BATCH_PROGRESS,
      meterBatchesLoaded: 0,
      meterBatchesTotal,
    });
  }

  // Dispatch initial progress so the overlay shows batch count immediately
  if (dispatchFunc && !cancelledRef?.current) {
    dispatchFunc({
      type: PortfolioShowLoadingActions.SET_TRADE_SUMMARY_PROGRESS,
      batchesLoaded: 0,
      batchesTotal: batchCount,
    });
  }

  // ── Concurrency limiter (Stages 2–5) ────────────────────────────────────────
  // Created here, after Stage 1 supplies rulesCount and metersCount, so the cap
  // can be derived from the actual portfolio size. Stage 1 runs unbounded.
  // The limiter is per-invocation: two overlapping loads each get their own cap.
  // Use tradeSummaryRulesCount (rulesCount ?? MAX_RULES_PER_PAGE) so that unknown
  // rulesCount on the date-range reload path falls back conservatively instead of
  // defaulting to 0, which would make recordsPerBatch=0 and open the cap to
  // MAX_CONCURRENCY — the least conservative outcome.
  logger.info(
    `fetchTimeSeriesStages: concurrency cap=${concurrencyCap} `
    + `(rulesCount=${tradeSummaryRulesCount}${rulesCount == null ? ' [fallback]' : ''}, metersCount=${metersCount}, `
    + `batchCount=${batchCount}, sourceBatchCount=${sourceBatchCount}, `
    + `tradeSourcePageSize=${tradePlan.sourcePageSize}, `
    + `tradeIntervalsPerBatch=${tradePlan.intervalsPerBatch}, `
    + `historianTimeBatchCount=${historianTimeBatchCount}, `
    + `recordsPerBatch=${batchCalculationResult.recordsPerBatch}, `
    + `peakHistorianSources=${concurrencyCap * sourcePageSize})`,
  );
  const limiter = createConcurrencyLimiter(concurrencyCap);
  // ────────────────────────────────────────────────────────────────────────────

  // Stages 2-5: All four streams start concurrently via Promise.all.
  // Stage 2 uses rulesMeterPageSize (rule-density-aware). Stages 3 & 4 enqueue
  // precomputed source batches and maximum-length historian windows together.
  // rulesPageSize uses the known rulesCount as a tight upper bound and falls back to
  // FALLBACK_RULES_PAGE_SIZE when rulesCount is unavailable.
  const rulesPageSize = Math.max(rulesCount ?? FALLBACK_RULES_PAGE_SIZE, 1);
  const rulesPromise = getStagePages({
    query: portfolioRulesPageQuery,
    baseVariables: { ...commonVariables, rulesPageSize },
    pageSize: rulesMeterPageSize,
    stageName: 'Stage 2 (Rules)',
    queryType: PORTFOLIO_SHOW_QUERY_TYPES.RULES,
    reshapeFunc: (edges) => getRulesPageData(edges),
    environment,
    cancelledRef,
    stageAbortRef,
    progressRef,
    meterBatchesTotal,
    dispatchFunc,
    limiter,
  });

  const historianPromise = getHistorianStagePages({
    commonVariables,
    sourcePages,
    timeBatches: historianTimeBatches,
    pageSize: sourcePageSize,
    historianAggregation,
    timezone,
    environment,
    cancelledRef,
    stageAbortRef,
    progressRef,
    meterBatchesTotal,
    dispatchFunc,
    limiter,
  });
  const consumedPromise = historianPromise.then(({ consumedBatches }) => (
    getMergedTimeSeriesBatches(consumedBatches, DATA_CONSUMED_KEY, startSeconds, finishSeconds)
  ));
  const generatedPromise = historianPromise.then(({ generatedBatches }) => (
    getMergedTimeSeriesBatches(generatedBatches, DATA_GENERATED_KEY, startSeconds, finishSeconds)
  ));

  // Stage 5: Prefer deterministic source-page/time-window requests whenever
  // Stage 1 supplied cursor metadata. Without cursors, retain the portfolio
  // time-batch query as a compatibility fallback for direct callers and older
  // cached meter payloads.
  let batchesCompleted = 0;
  const markTradeBatchComplete = () => {
    batchesCompleted += 1;
    logger.log(`Stage 5: Batch ${batchesCompleted}/${batchCount} complete`);
    if (dispatchFunc && !cancelledRef?.current && !stageAbortRef?.current) {
      dispatchFunc({
        type: PortfolioShowLoadingActions.SET_TRADE_SUMMARY_PROGRESS,
        batchesLoaded: batchesCompleted,
        batchesTotal: batchCount,
      });
    }
  };
  const tradeSummaryPromise = useSourceFirstTradeSummary
    ? getPrecomputedTradeSummaryBatches({
      portfolioId,
      sourcePages: tradeSourcePages,
      timeBatches: tradeTimeBatches,
      startSeconds,
      finishSeconds,
      environment,
      tradeAggregation,
      timezone,
      cancelledRef,
      stageAbortRef,
      limiter,
      onBatchCompleteFunc: markTradeBatchComplete,
    })
    : Promise.all(
      timeBatches.map((batch) => getPortfolioTradeSetSummaryBatch({
        portfolioId,
        batchStart: Math.floor(batch.start.toSeconds()),
        batchFinish: Math.floor(batch.finish.toSeconds()),
        environment,
        tradeAggregation,
        timezone,
        cancelledRef,
        stageAbortRef,
        limiter,
      }).then((batchResult) => {
        markTradeBatchComplete();
        return batchResult;
      })),
    );

  // Run all five stages in parallel: Stages 2-4 (rules, consumed, generated)
  // and Stage 5 (trade summary) start concurrently.
  const [rulesData, consumedData, generatedData, tradeSummaryBatches] = await Promise.all([
    rulesPromise.then((data) => {
      const totalRules = getTotalRulesCount(data);
      if (dispatchFunc && !cancelledRef?.current && !stageAbortRef?.current) {
        dispatchFunc({
          type: PortfolioShowLoadingActions.SET_RULES_LOADED,
          rulesCount: totalRules,
        });
      }
      return data;
    }),
    consumedPromise.then((data) => {
      logger.log(`Stage 3 complete: ${data.length} consumed data sets`);
      if (dispatchFunc && !cancelledRef?.current && !stageAbortRef?.current) {
        dispatchFunc({ type: PortfolioShowLoadingActions.SET_CONSUMED_DATA_LOADED });
      }
      return data;
    }),
    generatedPromise.then((data) => {
      logger.log(`Stage 4 complete: ${data.length} generated data sets`);
      if (dispatchFunc && !cancelledRef?.current && !stageAbortRef?.current) {
        dispatchFunc({ type: PortfolioShowLoadingActions.SET_GENERATED_DATA_LOADED });
      }
      return data;
    }),
    tradeSummaryPromise,
  ]);
  // Final request settlement: the start of the post-fetch processing window.
  const settledAt = nowMs();

  if (cancelledRef?.current) {
    logger.log('Portfolio data fetch cancelled after parallel stages');
    return null;
  }

  const totalRulesCount = getTotalRulesCount(rulesData);

  // Approximate total GraphQL requests issued by stages 2–5.
  const stageRequestCount = rulesPages + (2 * historianRequestsPerStage) + batchCount;

  return {
    rulesData,
    consumedData,
    generatedData,
    tradeSummaryBatches,
    totalRulesCount,
    batchCount,
    stageRequestCount,
    settledAt,
  };
};

/**
 * Fetch time-series data (stages 2-5) for a date-range change, reusing
 * pre-fetched meters. Called by the cache layer on a miss.
 * @param {object} args
 * @param {string} args.portfolioId - Portfolio UUID
 * @param {Array}  args.meters - Pre-fetched meter objects
 * @param {object} args.timeRange - { start, finish } Luxon DateTimes
 * @param {string} args.timezone - Timezone string
 * @param {object} args.environment - Relay environment
 * @param {string} args.historianAggregation - Aggregation for energy data
 * @param {string} args.tradeAggregation - Aggregation for trade data
 * @param {object} args.initialData - Initial portfolio data (static fields)
 * @param {Function} [args.dispatchFunc] - Reducer dispatch
 * @param {object}   [args.cancelledRef] - Cancellation token
 * @param {Function} [args.onDataReceived] - Callback with (mergedData, tradeSummaryBatches, meters,
 *   settledAt), where settledAt is the performance.now() time the last request settled
 * @param {Function} [args.onError] - Callback on error
 * @param {object}   [args.isFetchingRef] - Ref to prevent parallel fetches
 * @returns {Promise<void>}
 */
export const getTimeSeriesData = async (args) => {
  const {
    portfolioId, meters, meterCursors, timeRange, timezone, environment,
    historianAggregation, tradeAggregation, initialData,
    dispatchFunc, cancelledRef, onDataReceived, onError, isFetchingRef,
  } = args;

  if (cancelledRef?.current) {
    logger.log('getTimeSeriesData cancelled before starting');
    return;
  }

  if (isFetchingRef && isFetchingRef.current) {
    logger.log('Skipping getTimeSeriesData - another fetch is already in progress');
    return;
  }

  if (isFetchingRef) {
    isFetchingRef.current = true;
  }

  const fetchStartMs = Date.now();

  try {
    const result = await fetchTimeSeriesStages({
      portfolioId,
      initialData,
      meters,
      meterCursors,
      timeRange,
      timezone,
      environment,
      historianAggregation,
      tradeAggregation,
      dispatchFunc,
      cancelledRef,
    });

    if (!result) return; // cancelled

    const mergedData = mergePortfolioBatchedData(
      initialData,
      meters,
      result.rulesData,
      result.consumedData,
      result.generatedData,
      result.tradeSummaryBatches,
    );

    if (dispatchFunc) {
      dispatchFunc({
        type: PortfolioShowLoadingActions.MARK_FULLY_LOADED,
        metersCount: meters.length,
        rulesCount: result.totalRulesCount,
        batchesLoaded: result.batchCount,
        batchesTotal: result.batchCount,
      });
    }

    logger.info(
      `getTimeSeriesData: complete in ${((Date.now() - fetchStartMs) / 1000).toFixed(2)}s `
      + `(meters=${meters.length}, rules=${result.totalRulesCount}, batches=${result.batchCount}, `
      + `requests=${result.stageRequestCount})`,
    );

    if (onDataReceived) {
      onDataReceived(mergedData, result.tradeSummaryBatches, meters, result.settledAt);
    }
  } catch (error) {
    logger.error(
      `Error in getTimeSeriesData after ${((Date.now() - fetchStartMs) / 1000).toFixed(2)}s:`,
      error,
    );
    if (onError) {
      onError(error, 'Error fetching portfolio data. Please try again.');
    }
  } finally {
    if (isFetchingRef) {
      isFetchingRef.current = false;
    }
  }
};

/**
 * Main orchestrator: Fetch all portfolio data in batches using the 5-stage pattern.
 * Stage 1: Paginated meters
 * Stages 2-4: Rules, consumed, and generated per meter (parallel across stages)
 * Stage 5: Source-first trade set summary with a portfolio-level fallback
 * @param {object} args - Arguments for the orchestration
 * @param {string} args.portfolioId - Portfolio UUID
 * @param {object} args.timeRange - Date span { start, finish } (Luxon DateTimes)
 * @param {string} args.timezone - Timezone string
 * @param {object} args.environment - Relay environment
 * @param {string} args.historianAggregation - Aggregation for energy data
 * @param {string} args.tradeAggregation - Aggregation for trade data
 * @param {object} args.initialData - Initial portfolio data (static fields)
 * @param {Function} args.onDataReceived - Callback with (mergedData, tradeSummaryBatches, meters,
 *   settledAt), where settledAt is the performance.now() time the last request settled
 * @param {Function} args.onError - Callback on error
 * @param {Function} [args.dispatchFunc] - Reducer dispatch for loading state updates
 * @param {object} [args.isFetchingRef] - Ref to prevent parallel fetches
 * @param {object} [args.cancelledRef] - Ref whose .current indicates cancellation
 */
export const getPortfolioDataInBatches = async (args) => {
  const {
    portfolioId,
    timeRange,
    timezone,
    environment,
    historianAggregation,
    tradeAggregation,
    initialData,
    onDataReceived,
    onError,
    dispatchFunc,
    isFetchingRef,
    cancelledRef,
  } = args;

  if (!portfolioId
    || !timeRange
    || !timezone
    || !environment
    || !historianAggregation
    || !tradeAggregation) {
    if (onError) {
      onError(new Error('Missing required parameters for getPortfolioDataInBatches'));
    }
    return;
  }

  if (cancelledRef && cancelledRef.current) {
    logger.log('Skipping portfolio fetch - request already cancelled');
    return;
  }

  if (isFetchingRef && isFetchingRef.current) {
    logger.log('Skipping portfolio fetch - another fetch is already in progress');
    return;
  }

  if (isFetchingRef) {
    isFetchingRef.current = true;
  }

  // Wall-clock start for total fetch duration. Declared outside the try so the
  // catch branch can report time-to-failure. Logged in seconds to make it easy
  // to compare load times across concurrency-cap settings.
  const fetchStartMs = Date.now();

  try {
    if (cancelledRef?.current) {
      logger.log('getPortfolioDataInBatches cancelled before starting');
      return;
    }

    // Portfolio data comes from the Relay fragment (already available).
    // Advance the state machine past LOADING_PORTFOLIO so the checklist
    // shows "Portfolio loaded" while meters are fetching.
    if (dispatchFunc) {
      dispatchFunc({ type: PortfolioShowLoadingActions.SET_PORTFOLIO_LOADED });
    }

    // Stage 1: Fetch all meters (paginated) and rule count in parallel.
    // Always use METER_LIST_PAGE_SIZE (500) as the default. For portfolios
    // with fewer meters, the backend returns them all in one page. Only
    // portfolios exceeding 500 meters will paginate across multiple requests.
    const { start, finish } = timeRange || {};
    const startSeconds = Math.floor(start.toSeconds());
    const finishSeconds = Math.floor(finish.toSeconds());

    const [{ meters, meterCursors }, rulesCount] = await Promise.all([
      getPortfolioMeters({
        portfolioId,
        environment,
        startSeconds,
        finishSeconds,
      }),
      getPortfolioRuleCount({
        portfolioId,
        environment,
        startSeconds,
        finishSeconds,
      }),
    ]);

    if (cancelledRef && cancelledRef.current) {
      logger.log('Cancelled after meters fetch, aborting');
      return;
    }

    if (meters.length === 0) {
      logger.log('No meters found, returning empty data');
      if (dispatchFunc) {
        dispatchFunc({
          type: PortfolioShowLoadingActions.MARK_FULLY_LOADED,
          metersCount: 0,
          rulesCount: 0,
          batchesLoaded: 0,
          batchesTotal: 0,
        });
      }
      if (onDataReceived) {
        onDataReceived(initialData, [], []);
      }
      return;
    }

    logger.log(`Stage 1 complete: ${meters.length} meters loaded, rulesCount=${rulesCount}`);
    if (dispatchFunc) {
      dispatchFunc({
        type: PortfolioShowLoadingActions.SET_METERS_LOADED,
        metersCount: meters.length,
      });
      // Signal that meters are displayed and parallel stages can begin.
      // This sets activeStages to [RULES, CONSUMED_DATA, GENERATED_DATA]
      // so the checklist shows them as ACTIVE during loading.
      dispatchFunc({ type: PortfolioShowLoadingActions.METERS_DISPLAYED });
    }

    if (cancelledRef?.current) {
      logger.log('getPortfolioDataInBatches cancelled after meters fetch');
      return;
    }

    // Stages 2–5: delegate to the shared helper (also used by getTimeSeriesData)
    const result = await fetchTimeSeriesStages({
      portfolioId,
      initialData,
      meters,
      meterCursors,
      timeRange,
      timezone,
      environment,
      historianAggregation,
      tradeAggregation,
      dispatchFunc,
      cancelledRef,
      rulesCount,
    });

    if (!result) return; // cancelled inside fetchTimeSeriesStages

    // Merge all stages into final data shape
    const mergedData = mergePortfolioBatchedData(
      initialData,
      meters,
      result.rulesData,
      result.consumedData,
      result.generatedData,
      result.tradeSummaryBatches,
    );

    if (dispatchFunc) {
      dispatchFunc({
        type: PortfolioShowLoadingActions.MARK_FULLY_LOADED,
        metersCount: meters.length,
        rulesCount: result.totalRulesCount,
        batchesLoaded: result.batchCount,
        batchesTotal: result.batchCount,
      });
    }

    if (cancelledRef && cancelledRef.current) {
      logger.log('Cancelled before delivering data, aborting');
      return;
    }

    // Stage 1: meter pages + 1 rulesCount request (run in parallel with meters).
    const stage1RequestCount = Math.ceil(meters.length / METER_LIST_PAGE_SIZE) + 1;
    const totalRequestCount = stage1RequestCount + result.stageRequestCount;
    logger.info(
      `getPortfolioDataInBatches: complete in ${((Date.now() - fetchStartMs) / 1000).toFixed(2)}s `
      + `(meters=${meters.length}, rules=${result.totalRulesCount}, batches=${result.batchCount}, `
      + `requests=${totalRequestCount})`,
    );

    if (onDataReceived) {
      onDataReceived(mergedData, result.tradeSummaryBatches, meters, result.settledAt);
    }
  } catch (error) {
    logger.error(
      `Error in portfolio data fetching after ${((Date.now() - fetchStartMs) / 1000).toFixed(2)}s:`,
      error,
    );
    if (onError) {
      onError(error, 'Error fetching portfolio data. Please try again.');
    }
  } finally {
    if (isFetchingRef) {
      isFetchingRef.current = false;
    }
  }
};
